import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { transformRoute, useTranslate } from "helpers";

import { useSelectedItemsChanges } from "ui/components/Table/hooks";

import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";

import { NoResults } from "ui/components/NoResults";
import { Loader } from "ui/components/Loader";

import { Routes as FinanceRoutes } from "ui/modules/finance/navigation";
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";

import { Dialog } from "ui/components/Dialog";

import { getFleetVehicleContractRentsAPI } from "services/api/fleet/vehicles/contracts/api";

import { Routes as ContactRoutes } from "ui/modules/contacts/navigation";
import ContractRentSearchFilters from "../ContractRentSearchFilters/ContractRentSearchFilters";
import ContractRentSearchResults from "../ContractRentSearchResults/ContractRentSearchResults";
import VehicleContractRentEditCard from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleContractRentEditCard/VehicleContractRentEditCard";
import { ContractRentSearchPageCmp, ContractRentSearchPageProps } from "./types";
import { FleetVehicleContractRentRow } from "services/api/fleet/vehicles/contracts/types";
import { ContractRentSearchFormValues } from "services/api/contracts/types";

const ContractRentSearchPage: ContractRentSearchPageCmp = (
    props: ContractRentSearchPageProps
) => {

    const { t } = useTranslate();

    const navigate = useNavigate();

    const [searchResults, setSearchResults] = useState<
        DataWithPagination<FleetVehicleContractRentRow>
    >(initialDataWithPagination);

    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [contractId, setContractId] = useState<string>("");
    const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [filters, setFilters] = useState<ContractRentSearchFormValues>({
        search: null, contractRentStatusId: null, contractDateFrom: null, contractDateTo: null,
        sellingPriceMin: null, sellingPriceMax: null, contactClientId: null,
    });

    useEffect(() => {
        fetchSearchResult(searchResults.pagination, filters);
    }, []);

    const fetchSearchResult = async (pagination: Pagination, filterValues?: ContractRentSearchFormValues) => {
        setIsLoading(true);
        try {
            const f = filterValues ?? filters;
            const results = await getFleetVehicleContractRentsAPI(pagination, {
                contractRentStatusId: f.contractRentStatusId ?? undefined,
                search: f.search ?? undefined,
                contractDateFrom: f.contractDateFrom,
                contractDateTo: f.contractDateTo,
                sellingPriceMin: f.sellingPriceMin,
                sellingPriceMax: f.sellingPriceMax,
                contactClientId: f.contactClientId ?? undefined,
            });
            setSearchResults(results);
        } catch {
            setSearchResults(initialDataWithPagination);
            setIsLoading(false);
            return;
        }
        setIsLoading(false);
    };

    const handleRowClick = useCallback(async (id: string) => {
        setContractId(id);
        setShowAddNewModal(true);
    }, []);

    const handleAddNew = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            setContractId("");
            setShowAddNewModal(true);
        },
        []
    );

    const handleEditSuccess = useCallback(() => {
        fetchSearchResult(searchResults.pagination);
        setContractId("");
        setShowAddNewModal(false);
    }, []);

    const handleSearchChange = useCallback((formValues: ContractRentSearchFormValues) => {
        setFilters(formValues);
        fetchSearchResult(searchResults.pagination, formValues);
    }, [searchResults.pagination]);

    const handleSelectedChange = useSelectedItemsChanges(
        selectedRows,
        setSelectedRows
    );

    const handlePaginationChange = (newPagination: Pagination) => {
        fetchSearchResult(newPagination);
    };

    const handleDeleteClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
        event.stopPropagation();
        event.preventDefault();

        setContractId(id);
        setOpenDialog(true);
    }

    const handleContractDeleteDialogConfirm = useCallback(async () => {
        if (!contractId) return;

        try {
            setOpenDialog(false);
            setContractId("");
        } catch (err) {
            console.error("Greška prilikom brisanja ugovora:", err);
        }
    }, [contractId, t]);

    const handleContractDeleteDialogClose = useCallback(() => {
        setContractId("");
        setOpenDialog(false);
    }, []);

    const handleVehicleClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
        event.preventDefault();
        event.stopPropagation();

        navigate(
            transformRoute(
                FleetVehicleRoutes.FleetVehicleDetailPageRouter,
                {
                    id: id,
                }
            )
        );
    }

    const handleContactClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
        event.preventDefault();
        event.stopPropagation();

        navigate(
            transformRoute(
                ContactRoutes.ContactDetailPageRouter,
                {
                    id: id,
                }
            )
        );
    }

    return (
        <>
            <div className="px-0 py-6 mx-[-1px] xl:px-6">
                <ContractRentSearchFilters
                    onAddNew={handleAddNew}
                    onSearchChange={handleSearchChange}
                />
                {isLoading && searchResults.data.length === 0 ? (
                    <div className="relative min-h-[200px]">
                        <Loader isLoading={true}>{null}</Loader>
                    </div>
                ) : !isLoading && searchResults.pagination.totalPages === 0 ? (
                    <NoResults message="Unesite ugovor klikom na 'Dodaj ugovor'" />
                ) : (
                    <ContractRentSearchResults
                        searchResults={searchResults}
                        selectedRows={selectedRows}
                        isLoading={isLoading}
                        onRowClick={handleRowClick}
                        onPaginationChange={handlePaginationChange}
                        onSelectedChange={handleSelectedChange}
                        onVehicleClick={handleVehicleClick}
                        onContactClick={handleContactClick}
                        onDeleteClick={handleDeleteClick}
                    />
                )}
            </div>

            <Dialog
                open={openDialog}
                title={t(
                    "admin.finance.contract.rent.dialog.delete.title"
                )}
                message={t(
                    "admin.finance.contract.rent.dialog.delete.message"
                )}
                confirmText={t(
                    "admin.finance.contract.rent.dialog.delete.confirmtext"
                )}
                cancelText={t(
                    "admin.finance.contract.rent.dialog.delete.canceltext"
                )}
                onConfirm={handleContractDeleteDialogConfirm}
                onCancel={handleContractDeleteDialogClose}
            />
            <VehicleContractRentEditCard
                show={showAddNewModal}
                vehicleId={""}
                contractId={contractId}
                onSuccess={handleEditSuccess}
                onClose={handleEditSuccess}
            />
        </>
    );
};

ContractRentSearchPage.route =
    FinanceRoutes.ContractSearchPageRouter;

export default ContractRentSearchPage;
